import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, Save, X, MessageSquare, TestTube } from 'lucide-react';
import CanonicalPhraseManager from './CanonicalPhraseManager';
import PhraseTestInterface from './PhraseTestInterface';

interface ActionType {
  id: string;
  tool_name: string;
  action_type: string;
  label: string;
  color: string;
  icon_key: string;
  priority: number;
  enabled: boolean;
  scope: 'inbound' | 'outbound' | 'both';
  created_at: string;
  updated_at: string;
}

const ICON_OPTIONS = [
  'Clock', 'Phone', 'Calendar', 'MessageSquare', 'PhoneOff', 'PhoneX', 
  'CheckCircle', 'AlertTriangle', 'Info', 'Star', 'Heart', 'User'
];

const COLOR_OPTIONS = [
  { label: 'Blue', value: 'bg-blue-500/10 text-blue-700 border-blue-200' },
  { label: 'Green', value: 'bg-green-500/10 text-green-700 border-green-200' },
  { label: 'Purple', value: 'bg-purple-500/10 text-purple-700 border-purple-200' },
  { label: 'Orange', value: 'bg-orange-500/10 text-orange-700 border-orange-200' },
  { label: 'Red', value: 'bg-red-500/10 text-red-700 border-red-200' },
  { label: 'Yellow', value: 'bg-yellow-500/10 text-yellow-700 border-yellow-200' },
  { label: 'Gray', value: 'bg-gray-500/10 text-gray-700 border-gray-200' }
];

export default function ActionTypesManagement() {
  const { toast } = useToast();
  const [actionTypes, setActionTypes] = useState<ActionType[]>([]);
  const [filteredActionTypes, setFilteredActionTypes] = useState<ActionType[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [expandedPhrases, setExpandedPhrases] = useState<string | null>(null);
  const [showTestInterface, setShowTestInterface] = useState(false);
  const [scopeFilter, setScopeFilter] = useState<'all' | 'inbound' | 'outbound'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    tool_name: '',
    action_type: '',
    label: '',
    color: COLOR_OPTIONS[0].value,
    icon_key: ICON_OPTIONS[0],
    priority: 0,
    enabled: true,
    scope: 'both' as 'inbound' | 'outbound' | 'both'
  });

  useEffect(() => {
    fetchActionTypes();
  }, []);

  // Filter and search effect
  useEffect(() => {
    let filtered = actionTypes;

    // Apply scope filter
    if (scopeFilter !== 'all') {
      filtered = filtered.filter(actionType => 
        actionType.scope === scopeFilter || actionType.scope === 'both'
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(actionType =>
        actionType.label.toLowerCase().includes(query) ||
        actionType.tool_name.toLowerCase().includes(query) ||
        actionType.action_type.toLowerCase().includes(query)
      );
    }

    setFilteredActionTypes(filtered);
  }, [actionTypes, scopeFilter, searchQuery]);

  const fetchActionTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_action_types')
        .select('*')
        .order('priority', { ascending: true });

      if (error) throw error;
      
      // Type the response properly since scope might be null for existing records
      const typedData = (data || []).map(item => ({
        ...item,
        scope: (item.scope as 'inbound' | 'outbound' | 'both') || 'both'
      }));
      
      setActionTypes(typedData);
    } catch (error) {
      console.error('Error fetching action types:', error);
      toast({
        title: "Error",
        description: "Failed to load action types",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const { error } = await supabase
        .from('admin_action_types')
        .insert([formData]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Action type created successfully",
      });

      setIsCreateDialogOpen(false);
      setFormData({
        tool_name: '',
        action_type: '',
        label: '',
        color: COLOR_OPTIONS[0].value,
        icon_key: ICON_OPTIONS[0],
        priority: 0,
        enabled: true,
        scope: 'both'
      });
      fetchActionTypes();
    } catch (error) {
      console.error('Error creating action type:', error);
      toast({
        title: "Error",
        description: "Failed to create action type",
        variant: "destructive",
      });
    }
  };

  const handleUpdate = async (id: string, updates: Partial<ActionType>) => {
    try {
      const { error } = await supabase
        .from('admin_action_types')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Action type updated successfully",
      });

      setEditingId(null);
      fetchActionTypes();
    } catch (error) {
      console.error('Error updating action type:', error);
      toast({
        title: "Error",
        description: "Failed to update action type",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this action type?')) return;

    try {
      const { error } = await supabase
        .from('admin_action_types')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Action type deleted successfully",
      });

      fetchActionTypes();
    } catch (error) {
      console.error('Error deleting action type:', error);
      toast({
        title: "Error",
        description: "Failed to delete action type",
        variant: "destructive",
      });
    }
  };

  const handleToggleEnabled = async (id: string, enabled: boolean) => {
    await handleUpdate(id, { enabled });
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-muted rounded w-1/3 mb-6"></div>
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Action Types Configuration</h2>
          <p className="text-muted-foreground">
            Configure display metadata for call action types from webhook tool calls
          </p>
        </div>
        
        <div className="flex space-x-2">
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Action Type
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Action Type</DialogTitle>
                <DialogDescription>
                  Add a new action type configuration for webhook tool calls
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="tool_name">Tool Name</Label>
                  <Input
                    id="tool_name"
                    value={formData.tool_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, tool_name: e.target.value }))}
                    placeholder="e.g., log_consent"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="action_type">Action Type</Label>
                  <Input
                    id="action_type"
                    value={formData.action_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, action_type: e.target.value }))}
                    placeholder="e.g., consent_callback"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="label">Display Label</Label>
                  <Input
                    id="label"
                    value={formData.label}
                    onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                    placeholder="e.g., Callback Consent"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="scope">Scope</Label>
                  <Select value={formData.scope} onValueChange={(value: 'inbound' | 'outbound' | 'both') => setFormData(prev => ({ ...prev, scope: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">Both (Inbound & Outbound)</SelectItem>
                      <SelectItem value="inbound">Inbound Only</SelectItem>
                      <SelectItem value="outbound">Outbound Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="color">Color Theme</Label>
                  <Select value={formData.color} onValueChange={(value) => setFormData(prev => ({ ...prev, color: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COLOR_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="icon_key">Icon</Label>
                  <Select value={formData.icon_key} onValueChange={(value) => setFormData(prev => ({ ...prev, icon_key: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ICON_OPTIONS.map(icon => (
                        <SelectItem key={icon} value={icon}>
                          {icon}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Input
                    id="priority"
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreate}>Create</Button>
              </div>
            </DialogContent>
          </Dialog>
          
          <Button 
            variant="outline" 
            onClick={() => setShowTestInterface(!showTestInterface)}
          >
            <TestTube className="h-4 w-4 mr-2" />
            {showTestInterface ? 'Hide' : 'Show'} Test Tool
          </Button>
        </div>
      </div>

      {/* Filter Pills and Search */}
      <div className="flex items-center justify-between gap-4 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Filter:</span>
          <div className="flex gap-1">
            {[
              { key: 'all', label: 'All Actions', count: actionTypes.length },
              { key: 'inbound', label: 'Inbound', count: actionTypes.filter(a => a.scope === 'inbound' || a.scope === 'both').length },
              { key: 'outbound', label: 'Outbound', count: actionTypes.filter(a => a.scope === 'outbound' || a.scope === 'both').length }
            ].map(({ key, label, count }) => (
              <Badge
                key={key}
                variant={scopeFilter === key ? "default" : "outline"}
                className="cursor-pointer hover:bg-primary/80"
                onClick={() => setScopeFilter(key as 'all' | 'inbound' | 'outbound')}
              >
                {label} ({count})
              </Badge>
            ))}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search actions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-64"
          />
        </div>
      </div>

      {showTestInterface && (
        <PhraseTestInterface />
      )}

      <div className="space-y-4">
        {filteredActionTypes.map((actionType) => (
          <Card key={actionType.id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-full ${actionType.color}`}>
                    <span className="text-sm font-mono">{actionType.icon_key}</span>
                  </div>
                  <div>
                    <CardTitle className="text-lg">{actionType.label}</CardTitle>
                    <CardDescription className="flex items-center gap-2">
                      <span>
                        Tool: <code className="text-xs bg-muted px-1 rounded">{actionType.tool_name}</code> → 
                        Type: <code className="text-xs bg-muted px-1 rounded ml-1">{actionType.action_type}</code>
                      </span>
                      <Badge 
                        variant="secondary" 
                        className="text-xs"
                      >
                        {actionType.scope}
                      </Badge>
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="outline">Priority: {actionType.priority}</Badge>
                  <Switch
                    checked={actionType.enabled}
                    onCheckedChange={(checked) => handleToggleEnabled(actionType.id, checked)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedPhrases(
                      expandedPhrases === actionType.id ? null : actionType.id
                    )}
                  >
                    <MessageSquare className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingId(actionType.id)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(actionType.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            {editingId === actionType.id && (
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tool Name</Label>
                    <Input
                      value={actionType.tool_name}
                      onChange={(e) => setActionTypes(prev => 
                        prev.map(at => at.id === actionType.id ? { ...at, tool_name: e.target.value } : at)
                      )}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Action Type</Label>
                    <Input
                      value={actionType.action_type}
                      onChange={(e) => setActionTypes(prev => 
                        prev.map(at => at.id === actionType.id ? { ...at, action_type: e.target.value } : at)
                      )}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Display Label</Label>
                    <Input
                      value={actionType.label}
                      onChange={(e) => setActionTypes(prev => 
                        prev.map(at => at.id === actionType.id ? { ...at, label: e.target.value } : at)
                      )}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Scope</Label>
                    <Select 
                      value={actionType.scope} 
                      onValueChange={(value: 'inbound' | 'outbound' | 'both') => setActionTypes(prev => 
                        prev.map(at => at.id === actionType.id ? { ...at, scope: value } : at)
                      )}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="both">Both (Inbound & Outbound)</SelectItem>
                        <SelectItem value="inbound">Inbound Only</SelectItem>
                        <SelectItem value="outbound">Outbound Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Input
                      type="number"
                      value={actionType.priority}
                      onChange={(e) => setActionTypes(prev => 
                        prev.map(at => at.id === actionType.id ? { ...at, priority: parseInt(e.target.value) || 0 } : at)
                      )}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Color Theme</Label>
                    <Select 
                      value={actionType.color} 
                      onValueChange={(value) => setActionTypes(prev => 
                        prev.map(at => at.id === actionType.id ? { ...at, color: value } : at)
                      )}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COLOR_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Icon</Label>
                    <Select 
                      value={actionType.icon_key} 
                      onValueChange={(value) => setActionTypes(prev => 
                        prev.map(at => at.id === actionType.id ? { ...at, icon_key: value } : at)
                      )}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ICON_OPTIONS.map(icon => (
                          <SelectItem key={icon} value={icon}>
                            {icon}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end space-x-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingId(null)}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleUpdate(actionType.id, actionType)}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                </div>
              </CardContent>
            )}
            
            {expandedPhrases === actionType.id && (
              <CardContent className="pt-0 border-t">
                <CanonicalPhraseManager 
                  actionTypeId={actionType.id}
                  actionTypeLabel={actionType.label}
                />
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {filteredActionTypes.length === 0 && !loading && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <h3 className="text-lg font-semibold mb-2">
              {searchQuery || scopeFilter !== 'all' ? 'No Matching Actions Found' : 'No Action Types Configured'}
            </h3>
            <p className="text-muted-foreground text-center mb-4">
              Create your first action type configuration to customize webhook tool call display metadata.
            </p>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Action Type
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}